// --- TimelineMark.tsx ---
import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import CommentIcon from '@mui/icons-material/ChatBubbleOutline';
import { Menu, MenuItem } from '@mui/material';
import { COMMENT } from '../../../enums';
import { useDrawingMarkers } from '../../VideoDrawing/context';
import { timeLineFormatTime } from '../../../helpers';
import styles from './TimelineMark.module.scss';
import { convertSeconds } from '../utils';

export type TTimelineMark = { value: number; markMargin: number; showValue?: boolean; isLast?: boolean };
export const TimelineMark: React.FC<TTimelineMark> = ({ value, markMargin, showValue, isLast }) => (
  <div
    className={cx(styles.timelineMark, showValue && styles.timelineMarkWithNumber)}
    style={{ marginLeft: value === 0 ? 0 : `${markMargin}px` }}
  >
    {showValue && (
      <div className={cx(styles.timelineMarkNumber, isLast && styles.lastTimelineMarkNumber)}>{convertSeconds(value, true)}</div>
    )}
  </div>
);

export type TTimeMarks = { seconds: number; zoom: number; markMargin: number };
export const TimeMarks: React.FC<TTimeMarks> = ({ seconds, zoom, markMargin }) => {
  const { markers, showMarker, videoComments = [], showVideoComment } = useDrawingMarkers();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; comments: COMMENT[] } | null>(null);
  const groups = useMemo(() => {
    const result = new Map<number, COMMENT[]>();
    [...markers, ...videoComments].filter((comment) => comment.clip_id == null && comment.parent_type !== 'clip' &&
      comment.timestamp >= 0 && comment.timestamp <= seconds).forEach((comment) => {
      const group = result.get(comment.timestamp) || [];
      group.push(comment);
      result.set(comment.timestamp, group);
    });
    return [...result.entries()].sort(([a], [b]) => a - b);
  }, [markers, videoComments, seconds]);
  const reveal = (comment: COMMENT) => {
    setMenu(null);
    if (comment.drawing) showMarker(comment);
    else showVideoComment(comment);
  };
  const count = useMemo(() => {
    if (zoom <= 0 || !isFinite(seconds / zoom)) return 0;
    return Math.floor(seconds / zoom);
  }, [seconds, zoom]);

  return (
    <>
      {count > 0 && Array.from({ length: count + 1 }, (_, i) => {
        const value = i * zoom;
        let show = false;
        if (value === 0) show = true;
        else if (seconds <= 600 && i % 2 === 0) show = true;
        else if (zoom > 29 && i % 2 === 0) show = true;
        else if (zoom >= 6 && zoom <= 29 && i % 2 === 0) show = true;
        else if (zoom < 6 && i % 2 === 0) show = true;
        return <TimelineMark key={value} value={value} isLast={count === i} markMargin={markMargin} showValue={show} />;
      })}
      {zoom > 0 && groups.map(([timestamp, comments]) => {
        const drawingOnly = comments.every((comment) => !!comment.drawing);
        const label = comments.length > 1 ? `${comments.length} video comments` : drawingOnly ? 'Video drawing' : 'Video comment';
        return <button key={timestamp} type="button" className={cx(styles.videoDrawingMarker, !drawingOnly && styles.videoCommentMarker)}
          aria-label={`${label} at ${timeLineFormatTime(timestamp)}`} title={`${label} · ${timeLineFormatTime(timestamp)}`}
          data-preserve-clip-workspace data-preserve-clip-edit
          style={{ left: Math.max(0, Math.min(seconds * (markMargin + 1) / zoom - 14, timestamp * (markMargin + 1) / zoom - 7)) }}
          onPointerDown={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (comments.length === 1) reveal(comments[0]);
            else setMenu({ anchor: event.currentTarget, comments });
          }}>{drawingOnly ? <DrawIcon /> : <CommentIcon />}
          {comments.length > 1 && <span className={styles.markerCount}>{comments.length}</span>}
        </button>;
      })}
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}
        data-preserve-clip-workspace data-preserve-clip-edit
        onPointerDown={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
        {menu?.comments.map((comment) => <MenuItem key={comment.id} onClick={(event) => { event.stopPropagation(); reveal(comment); }}>
          {comment.drawing ? <DrawIcon fontSize="small" /> : <CommentIcon fontSize="small" />}
          <span className={styles.markerPreview}>{comment.message?.replace(/<[^>]*>/g, '').trim() || (comment.drawing ? 'Drawing' : 'Comment')}</span>
        </MenuItem>)}
      </Menu>
    </>
  );
};
